import type { LocaleCode } from '@/i18n/locales'
import { DEFAULT_PERSONA } from '@/persona/personas'
import type { PersonaId } from '@/persona/personas'
import { SpeechEnvelope } from '../audio/level'
import type {
  ConnectOptions,
  ListenOptions,
  ListenResult,
  SpeakOptions,
  SpeakResult,
  VoiceCapabilities,
  VoiceEvent,
  VoiceListener,
  VoiceTransport,
} from '../types'
import { VoiceError } from '../types'

/**
 * A voice backend with no browser in it.
 *
 * This is not a stub. It is the reference implementation of the contract, and
 * it exists so that the entire product, including the onboarding conversation,
 * can be built and tested end to end before a single speech model ships. It
 * emits the same events in the same order as a real transport, including the
 * awkward ones: partial transcripts that change, speech that gets cut off
 * mid-word, and listens that time out with nothing.
 *
 * Tests drive it with `enqueueUserSpeech`. In the browser it also accepts typed
 * answers, so the app is usable on a device with no microphone at all.
 */

interface QueuedSpeech {
  text: string
  /** Delay before the user "starts talking". */
  delayMs: number
  confidence: number
  /** Simulates a recogniser that heard noise but no words. */
  unintelligible: boolean
}

export interface MockTransportOptions {
  /** Multiplier on all simulated durations. Tests use a large value. */
  speed?: number
  /** Fail `connect` once, to exercise the reconnect path. */
  failConnect?: boolean
  /** Report the assistant's speech as un-interruptible, like iOS. */
  fullDuplex?: boolean
}

/** Rough speaking pace, used to size simulated utterance duration. */
const MS_PER_CHARACTER = 52
const MIN_UTTERANCE_MS = 420
const LEVEL_INTERVAL_MS = 50

export class MockTransport implements VoiceTransport {
  readonly id = 'mock'

  private readonly listeners = new Set<VoiceListener>()
  private readonly queue: QueuedSpeech[] = []
  private readonly envelope = new SpeechEnvelope()
  private readonly speed: number
  private readonly duplex: boolean

  private locale: LocaleCode = 'en'
  private persona: PersonaId = DEFAULT_PERSONA
  private rate = 1
  private muted = false
  private connected = false
  private shouldFailConnect: boolean

  /**
   * An answer given by typing or by tapping a labelled choice, before the
   * assistant was ready to listen for it.
   *
   * People do not wait for a listening window. They tap "yes" while the
   * question is still being read out, or type an answer during the pause after
   * it. Dropping that input because no turn was open makes a visible, enabled
   * button silently do nothing, which is the least forgivable kind of bug in a
   * product whose whole promise is that it is listening.
   */
  private pendingAnswer: string | null = null

  private speechTimers: ReturnType<typeof setTimeout>[] = []
  private levelTimer: ReturnType<typeof setInterval> | null = null
  private activeSpeech: { resolve: (result: SpeakResult) => void; startedAt: number } | null = null
  /**
   * The turn that is open right now.
   *
   * `speaking` is set once queued speech has been handed to it, so a second
   * utterance arriving mid-turn waits for the next turn rather than being
   * played over the top of the first. `startTimer` is the silence timeout, held
   * separately so it can be cancelled the moment somebody starts talking rather
   * than only when the turn settles.
   */
  private activeListen: {
    resolve: (result: ListenResult) => void
    timers: ReturnType<typeof setTimeout>[]
    startTimer: ReturnType<typeof setTimeout> | null
    speaking: boolean
  } | null = null

  constructor(options: MockTransportOptions = {}) {
    this.speed = options.speed ?? 1
    this.shouldFailConnect = options.failConnect ?? false
    this.duplex = options.fullDuplex ?? true
  }

  get capabilities(): VoiceCapabilities {
    return {
      recognition: true,
      synthesis: true,
      inputLevel: true,
      fullDuplex: this.duplex,
      bargeIn: this.duplex,
      endpointing: true,
      duplex: false,
    }
  }

  // -- lifecycle ------------------------------------------------------------

  async connect(options: ConnectOptions): Promise<void> {
    this.locale = options.locale
    this.rate = options.rate ?? 1
    if (options.persona) this.persona = options.persona

    this.emit({ type: 'connection', status: 'connecting' })

    if (this.shouldFailConnect) {
      // Only the first attempt fails, so a retry succeeds and the reconnect
      // path can be exercised without reconfiguring the transport.
      this.shouldFailConnect = false
      const error = new VoiceError('network', 'The mock transport refused the first connection.')
      this.emit({ type: 'error', error })
      throw error
    }

    await this.wait(60)
    this.connected = true
    this.emit({ type: 'connection', status: 'open' })
  }

  async disconnect(): Promise<void> {
    this.connected = false
    this.cancelSpeech()
    this.abortListen()
    this.emit({ type: 'connection', status: 'closed' })
  }

  // -- speaking -------------------------------------------------------------

  speak(text: string, options: SpeakOptions = {}): Promise<SpeakResult> {
    this.cancelSpeech()

    const interruptible = options.interruptible ?? true
    const duration = this.utteranceDuration(text)
    const startedAt = this.now()

    return new Promise<SpeakResult>((resolve) => {
      this.activeSpeech = { resolve, startedAt }
      this.emit({ type: 'speech_start', speaker: 'namu' })
      this.envelope.start(startedAt)

      // The caption arrives as a whole line, the way a real transport delivers
      // the final text of an utterance it is about to speak.
      this.emit({ type: 'transcript', speaker: 'namu', text, final: true })

      this.startLevels('namu')

      // Word boundaries, so the orb's rhythm matches the sentence.
      const parts = text.split(/\s+/).filter(Boolean)
      let elapsed = 0
      for (const word of parts) {
        const share = (word.length / Math.max(1, text.length)) * duration
        elapsed += share
        const at = elapsed
        this.speechTimers.push(
          setTimeout(() => this.envelope.onWord(word.length, this.now()), Math.max(0, at)),
        )
      }

      this.speechTimers.push(
        setTimeout(() => {
          this.finishSpeech(true, interruptible)
        }, duration),
      )

      if (options.signal) {
        options.signal.addEventListener('abort', () => this.cancelSpeech(), { once: true })
      }
    })
  }

  cancelSpeech(): void {
    if (!this.activeSpeech) return
    this.finishSpeech(false, true)
  }

  private finishSpeech(completed: boolean, _interruptible: boolean): void {
    const active = this.activeSpeech
    if (!active) return
    this.activeSpeech = null

    for (const timer of this.speechTimers) clearTimeout(timer)
    this.speechTimers = []
    this.envelope.stop()
    this.stopLevels()

    this.emit({ type: 'speech_end', speaker: 'namu', interrupted: !completed })
    active.resolve({ completed, durationMs: this.now() - active.startedAt })
  }

  // -- listening ------------------------------------------------------------

  listen(options: ListenOptions = {}): Promise<ListenResult> {
    this.abortListen()

    // An answer given before the turn opened counts for this turn.
    if (this.pendingAnswer !== null) {
      const transcript = this.pendingAnswer
      this.pendingAnswer = null
      return Promise.resolve({ status: 'typed', transcript })
    }

    const startTimeout = options.startTimeoutMs ?? 8000
    const timers: ReturnType<typeof setTimeout>[] = []

    return new Promise<ListenResult>((resolve) => {
      // Nothing scripted yet means waiting for somebody to talk, or timing out,
      // which is exactly what happens in the browser when the user says
      // nothing. Armed before the queue is read, so the silence timeout covers
      // the whole turn rather than only the turns that open empty.
      const startTimer = setTimeout(() => {
        this.settleListen({ status: 'timeout' })
      }, this.scale(startTimeout))
      timers.push(startTimer)

      this.activeListen = { resolve, timers, startTimer, speaking: false }

      if (options.signal) {
        options.signal.addEventListener('abort', () => this.abortListen(), { once: true })
      }

      this.deliverQueued()
    })
  }

  /**
   * Hands whatever speech is queued to the turn that is open.
   *
   * Called both when a turn opens and when speech is queued, because the two
   * happen in either order and only one of those orders used to work. Speech
   * queued before the turn was read out of the queue as the turn opened; speech
   * queued a moment later, while the turn was already open and waiting, sat in
   * the queue untouched and the turn timed out as silence.
   *
   * That is not a difference a real recogniser has. It made a spec that paused
   * between two lines fail, and it made every spec in the suite quietly depend
   * on being fast enough to speak before the assistant stopped talking, which
   * is the opposite of the determinism the mock transport exists to provide.
   */
  private deliverQueued(): void {
    const active = this.activeListen
    // One utterance per turn. A second one arriving while the first is being
    // delivered belongs to the next turn, not on top of this one.
    if (!active || active.speaking) return

    const queued = this.queue.shift()
    if (!queued) return

    active.speaking = true
    // Somebody is talking now, so the turn is no longer silent.
    if (active.startTimer !== null) {
      clearTimeout(active.startTimer)
      active.startTimer = null
    }

    const timers = active.timers
    timers.push(
      setTimeout(() => {
        this.emit({ type: 'speech_start', speaker: 'user' })
        this.startLevels('user')

        if (queued.unintelligible) {
          timers.push(
            setTimeout(() => {
              this.stopLevels()
              this.emit({ type: 'speech_end', speaker: 'user', interrupted: false })
              this.settleListen({ status: 'unintelligible' })
            }, this.scale(600)),
          )
          return
        }

        // Partials, growing word by word, the way a streaming recogniser
        // delivers them. Components that render the live transcript are
        // exercised by this, not just the final result.
        const parts = queued.text.split(/\s+/).filter(Boolean)
        const perWord = this.scale(160)
        parts.forEach((_, index) => {
          timers.push(
            setTimeout(
              () => {
                this.emit({
                  type: 'transcript',
                  speaker: 'user',
                  text: parts.slice(0, index + 1).join(' '),
                  final: false,
                })
              },
              perWord * (index + 1),
            ),
          )
        })

        timers.push(
          setTimeout(
            () => {
              this.stopLevels()
              this.emit({ type: 'transcript', speaker: 'user', text: queued.text, final: true })
              this.emit({ type: 'speech_end', speaker: 'user', interrupted: false })
              this.settleListen({
                status: 'final',
                transcript: queued.text,
                confidence: queued.confidence,
                durationMs: perWord * parts.length,
              })
            },
            perWord * (parts.length + 1),
          ),
        )
      }, this.scale(queued.delayMs)),
    )
  }

  endTurn(): void {
    if (!this.activeListen) return
    this.stopLevels()
    this.settleListen({ status: 'timeout' })
  }

  private abortListen(): void {
    if (!this.activeListen) return
    this.stopLevels()
    this.settleListen({ status: 'aborted' })
  }

  private settleListen(result: ListenResult): void {
    const active = this.activeListen
    if (!active) return
    this.activeListen = null
    for (const timer of active.timers) clearTimeout(timer)
    active.resolve(result)
  }

  submitTyped(text: string): void {
    this.emit({ type: 'transcript', speaker: 'user', text, final: true })
    if (this.activeListen) {
      this.stopLevels()
      this.settleListen({ status: 'typed', transcript: text })
      return
    }
    // No turn open yet. Hold it for the next one rather than losing it.
    this.pendingAnswer = text
  }

  // -- settings -------------------------------------------------------------

  setMuted(muted: boolean): void {
    this.muted = muted
    if (muted) this.emit({ type: 'level', source: 'user', value: 0 })
  }

  setLocale(locale: LocaleCode): void {
    this.locale = locale
  }

  setPersona(persona: PersonaId): void {
    this.persona = persona
  }

  setRate(rate: number): void {
    this.rate = Math.min(2, Math.max(0.5, rate))
  }

  /** The locale currently configured. Read by tests. */
  get activeLocale(): LocaleCode {
    return this.locale
  }

  /** The persona currently configured. Read by tests. */
  get activePersona(): PersonaId {
    return this.persona
  }

  get isConnected(): boolean {
    return this.connected
  }

  // -- test and demo controls ----------------------------------------------

  /** Queues something the user will "say" on the next `listen()`. */
  enqueueUserSpeech(
    text: string,
    options: { delayMs?: number; confidence?: number; unintelligible?: boolean } = {},
  ): void {
    this.queue.push({
      text,
      delayMs: options.delayMs ?? 240,
      confidence: options.confidence ?? 0.94,
      unintelligible: options.unintelligible ?? false,
    })
    // Delivered straight away if a turn is already waiting. Without this a
    // caller had to enqueue before the assistant stopped speaking or the turn
    // heard nothing at all, which is a rule about the mock rather than about
    // anything a person or a recogniser does.
    this.deliverQueued()
  }

  /** Simulates the user talking over the assistant. */
  bargeIn(text: string): void {
    this.enqueueUserSpeech(text, { delayMs: 0 })
    this.cancelSpeech()
  }

  clearQueue(): void {
    this.queue.length = 0
  }

  // -- events ---------------------------------------------------------------

  subscribe(listener: VoiceListener): () => void {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  private emit(event: VoiceEvent): void {
    for (const listener of [...this.listeners]) listener(event)
  }

  // -- helpers --------------------------------------------------------------

  private startLevels(source: 'user' | 'namu'): void {
    this.stopLevels()
    let phase = 0
    this.levelTimer = setInterval(() => {
      phase += 1
      const value =
        source === 'namu'
          ? this.envelope.sample(this.now())
          : // A plausible speaking envelope: a slow swell with syllable ripple.
            this.muted
            ? 0
            : 0.35 + 0.3 * Math.abs(Math.sin(phase / 3)) + 0.15 * Math.abs(Math.sin(phase / 1.3))
      this.emit({ type: 'level', source, value: Math.min(1, value) })
    }, LEVEL_INTERVAL_MS)
  }

  private stopLevels(): void {
    if (this.levelTimer !== null) {
      clearInterval(this.levelTimer)
      this.levelTimer = null
    }
    this.emit({ type: 'level', source: 'user', value: 0 })
    this.emit({ type: 'level', source: 'namu', value: 0 })
  }

  private utteranceDuration(text: string): number {
    const raw = Math.max(MIN_UTTERANCE_MS, text.length * MS_PER_CHARACTER)
    return this.scale(raw / this.rate)
  }

  private scale(ms: number): number {
    return Math.max(0, Math.round(ms / this.speed))
  }

  private now(): number {
    return typeof performance !== 'undefined' ? performance.now() : Date.now()
  }

  private wait(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, this.scale(ms)))
  }
}
